import React from "react";
import Snackbar from "@mui/material/Snackbar";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";

type ToastVariant = "success" | "error" | "warning" | "info";

interface CommonSnackbarProps {
  open: boolean;
  message: string;
  onClose: () => void;
  backgroundColor?: string;
  classes?: string;
  autoHideDuration?: number;
  solid?: boolean;
  mono?: boolean;
}

const VARIANTS: Record<
  ToastVariant,
  { title: string; background: string; color: string; Icon: typeof InfoIcon }
> = {
  success: {
    title: "Success",
    background: "#E5F8ED",
    color: "#1E9E57",
    Icon: CheckCircleIcon,
  },
  info: {
    title: "Info",
    background: "#E7F0FB",
    color: "#3983DD",
    Icon: InfoIcon,
  },
  error: {
    title: "Error",
    background: "#FDECEC",
    color: "#E15B64",
    Icon: CancelIcon,
  },
  warning: {
    title: "Warning",
    background: "#FFF4D8",
    color: "#E0A100",
    Icon: WarningIcon,
  },
};

function resolveVariant(color?: string): ToastVariant {
  const value = (color || "").trim().toLowerCase();
  if (!value) return "info";

  if (
    value.includes("green") ||
    value === "#2e7d32" ||
    value === "#1b9a59" ||
    value === "#1f9d55"
  ) {
    return "success";
  }

  if (value.includes("red") || value === "#d32f2f" || value === "#d14343") {
    return "error";
  }

  if (
    value.includes("orange") ||
    value.includes("yellow") ||
    value.includes("amber") ||
    value.includes("warn") ||
    value === "#f57c00" ||
    value === "#fff4e5"
  ) {
    return "warning";
  }

  return "info";
}

interface ToastCardProps extends React.HTMLAttributes<HTMLDivElement> {
  message: string;
  variant: ToastVariant;
  duration: number;
  onClose: () => void;
}

const ToastCard = React.forwardRef<HTMLDivElement, ToastCardProps>(
  ({ message, variant, duration, onClose, style, ...other }, ref) => {
    const { title, background, color, Icon } = VARIANTS[variant];
    const barRef = React.useRef<HTMLSpanElement>(null);
    const onCloseRef = React.useRef(onClose);
    const hoveredRef = React.useRef(false);
    onCloseRef.current = onClose;

    React.useEffect(() => {
      let remaining = duration;
      let last = performance.now();
      let frame = 0;
      let closed = false;

      const tick = (now: number) => {
        if (!hoveredRef.current) {
          remaining -= now - last;
          const ratio = Math.max(remaining / duration, 0);
          if (barRef.current) {
            barRef.current.style.transform = `scaleX(${ratio})`;
          }
          if (remaining <= 0 && !closed) {
            closed = true;
            onCloseRef.current();
            return;
          }
        }
        last = now;
        frame = requestAnimationFrame(tick);
      };

      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    }, [duration]);

    return (
      <div
        ref={ref}
        role="status"
        {...other}
        onMouseEnter={(event) => {
          hoveredRef.current = true;
          other.onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          hoveredRef.current = false;
          other.onMouseLeave?.(event);
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "min(420px, calc(100vw - 32px))",
          padding: "14px 10px 16px 14px",
          overflow: "hidden",
          background,
          border: `1px solid ${color}33`,
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(21, 27, 45, 0.14)",
          ...style,
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "#FFFFFF",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 1px 2px rgba(21, 27, 45, 0.08)",
          }}
        >
          <Icon sx={{ fontSize: 18, color }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              color,
              fontSize: 15,
              lineHeight: 1.3,
              fontWeight: 700,
            }}
          >
            {title}
          </p>
          <p
            style={{
              margin: "2px 0 0",
              color,
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 400,
              wordBreak: "break-word",
            }}
          >
            {message}
          </p>
        </div>
        <IconButton
          size="small"
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#9AA0A6",
            padding: "4px",
            flexShrink: 0,
            alignSelf: "center",
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: "#151B2D",
              color: "#FFFFFF",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            height: 3,
            width: "100%",
            background: `${color}33`,
          }}
        />
        <span
          ref={barRef}
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            height: 3,
            width: "100%",
            background: color,
            transformOrigin: "left center",
            transform: "scaleX(1)",
          }}
        />
      </div>
    );
  },
);

ToastCard.displayName = "ToastCard";

const CommonSnackbar: React.FC<CommonSnackbarProps> = ({
  open,
  message,
  onClose,
  backgroundColor,
  autoHideDuration = 3000,
}) => {
  return (
    <Snackbar
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{
        "&.MuiSnackbar-anchorOriginTopRight": {
          right: 120,
        },
      }}
      disableWindowBlurListener
    >
      <ToastCard
        key={`${message}-${autoHideDuration}`}
        message={message}
        variant={resolveVariant(backgroundColor)}
        duration={autoHideDuration}
        onClose={onClose}
      />
    </Snackbar>
  );
};

export default CommonSnackbar;
